<script setup>
import ActionButton from "@js/components/ActionButton.vue";
import Drawer from "@js/components/Drawer.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import { Form } from "vee-validate";
import { reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { array, object } from "yup";
import InputField from "@js/components/fields/InputField.vue";
import WysiwygField from "@js/components/fields/WysiwygField.vue";

const { t } = useI18n();

const props = defineProps({
  fromOptions: {
    type: Array,
    required: true,
  },
  toOptions: {
    type: Array,
    required: true,
  },
  from: {
    type: Object,
    required: false,
  },
  to: {
    type: Array,
    required: false,
    default: () => [],
  },
  subject: {
    type: String,
    required: false,
    default: "",
  },
  body: {
    type: String,
    required: false,
    default: "",
  },
  callback: {
    type: Function,
    required: false,
  },
});

const form = reactive({
  from: props.from ? props.from : props.fromOptions[0],
  to: props.to,
  subject: props.subject,
  body: props.body,
});

const validationSchema = object({
  from: object().required(t("error.required")),
  to: array().min(1).required(t("error.required")),
});

const isOpen = ref(false);

const loading = ref(false);

function setLoading(value) {
  loading.value = value;
}

function setIsOpen(value) {
  isOpen.value = value;
}

function sendEmail() {
  loading.value = true;

  props.callback(form.from, form.to, form.subject, form.body, setLoading, setIsOpen);
}
</script>

<template>
  <slot name="button" :set-is-open="setIsOpen" />

  <Drawer :is-open="isOpen" :set-is-open="setIsOpen" size="large">
    <template #title>
      <div class="flex items-center">
        {{ t("title.sendEmail") }}
      </div>
    </template>

    <template #full-content>
      <Form
        :validation-schema="validationSchema"
        @submit="sendEmail"
        v-slot="{ errors }"
        class="flex h-full flex-col"
      >
        <div class="px-6 py-4 overflow-y-auto max-h-[calc(100vh-8rem)] h-full">
          <ListboxField
            v-model="form.from"
            field-id="from"
            field-name="from"
            :label="t('label.from')"
            :options="props.fromOptions"
          >
            <template #buttonContent>
              <template v-if="form.from">
                {{ form.from.name }}
              </template>

              <template v-else>
                &nbsp;
              </template>
            </template>

            <template #optionContent="{ option }">
              {{ option.name }} - {{ option.email }}
            </template>
          </ListboxField>

          <ListboxField
            v-model="form.to"
            field-id="to"
            field-name="to"
            :label="t('label.to')"
            :options="props.toOptions"
            multiple
          >
            <template #buttonContent>
              <template v-if="form.to.length > 0">
                {{ form.to.map((to) => to.name).join(", ") }}
              </template>

              <template v-else>
                &nbsp;
              </template>
            </template>

            <template #optionContent="{ option }">
              {{ option.name }} - {{ option.email }}
            </template>
          </ListboxField>

          <InputField
            v-model="form.subject"
            :label="t('label.subject')"
            field-name="subject"
            field-id="subject"
          />

          <WysiwygField
            v-model="form.body"
            :label="t('label.body')"
            field-name="body"
            field-id="body"
          />
        </div>

        <div
          class="mt-auto flex flex-wrap items-center gap-2 border-t border-t-gray-200 p-6"
        >
          <ActionButton
            submit
            :disabled="Object.keys(errors).length > 0 || loading"
            :loading="loading"
          >
            {{ t("button.send") }}
          </ActionButton>
        </div>
      </Form>
    </template>
  </Drawer>
</template>
